"use client";

import { cn } from "@/lib/utils";
import type { Participant } from "../base-match-template";
import {
  CourtSvg,
  courtLines,
  courtQuad,
  courtStripes,
  createCourtProjector,
  getRacketMatchState,
  PAINT_LINE,
  PropShadow,
  RacketMatchFrame,
  RacketScore,
  useSvgIdPrefix,
} from "./racket-court-kit";

export type TennisCourtPreviewProps = {
  mode: "singles" | "doubles";
  yourTeam: Participant[];
  opponentTeam: Participant[];
  yourWon?: boolean;
  showLabels?: boolean;
  linkToProfiles?: boolean;
  matchScore?: { yourSets: number; opponentSets: number };
  sets?: { yourScore: number; opponentScore: number }[];
};

// ─── Court geometry (grass court, elevated side camera) ─────────────────────
// 23.77m × 10.97m doubles court: alleys at 1.37m, service lines 6.40m off the
// net. The posts stand 0.914m outside the doubles sidelines and are drawn
// INSIDE the frame (the far cap sits just under the label pill).

const P = createCourtProjector({
  yFar: 50,
  yNear: 196,
  farHalfWidth: 114,
  nearHalfWidth: 140,
});

const ALLEY = 0.125;
const SERVICE = 0.231;
const POST_V = 0.083;

const COURT = courtQuad(P, 0, 0, 1, 1);
const SURROUND_STRIPES = courtStripes(P, 14, -0.7, 1.7, -0.35, 1.5);
const COURT_STRIPES = courtStripes(P, 12, 0, 1, 0, 1);
const OUTER_LINES = courtLines(P, [
  [0, 0, 1, 0],
  [1, 0, 1, 1],
  [1, 1, 0, 1],
  [0, 1, 0, 0],
]);
const INNER_LINES = courtLines(P, [
  [0, ALLEY, 1, ALLEY],
  [0, 1 - ALLEY, 1, 1 - ALLEY],
  [SERVICE, ALLEY, SERVICE, 1 - ALLEY],
  [1 - SERVICE, ALLEY, 1 - SERVICE, 1 - ALLEY],
  [SERVICE, 0.5, 1 - SERVICE, 0.5],
  [0, 0.5, 0.014, 0.5],
  [0.986, 0.5, 1, 0.5],
]);
const NET_SHADOW = courtQuad(P, 0.5, -POST_V, 0.522, 1 + POST_V);
const [, NET_FAR_Y] = P(0.5, -POST_V);
const [, NET_NEAR_Y] = P(0.5, 1 + POST_V);

function TennisCourtArt() {
  const id = useSvgIdPrefix();
  return (
    <>
      <CourtSvg>
        <defs>
          <linearGradient id={`${id}-wall`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#04130b" />
            <stop offset="1" stopColor="#0d3320" />
          </linearGradient>
          <linearGradient id={`${id}-runoff`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#3f7c2c" />
            <stop offset="1" stopColor="#1f4d1a" />
          </linearGradient>
          <linearGradient id={`${id}-court`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#79b84d" />
            <stop offset="0.55" stopColor="#5c9d3a" />
            <stop offset="1" stopColor="#3f7f2a" />
          </linearGradient>
          <pattern
            id={`${id}-mesh`}
            width="2"
            height="2.4"
            patternUnits="userSpaceOnUse"
          >
            <rect width="2" height="2.4" fill="rgba(12,20,14,0.55)" />
            <rect y="1.6" width="2" height="0.5" fill="rgba(255,255,255,0.4)" />
          </pattern>
        </defs>

        {/* Back wall: stadium green with the purple trim and ad boards. */}
        <rect width="300" height="46" fill={`url(#${id}-wall)`} />
        <rect y="26" width="300" height="11" fill="#103d26" />
        {[18, 64, 186, 232].map((x) => (
          <rect
            key={x}
            x={x}
            y="29"
            width="50"
            height="5"
            rx="1"
            fill="rgba(222,244,200,0.16)"
          />
        ))}
        <rect y="37" width="300" height="2.5" fill="#5b2c83" />

        {/* Run-off grass with its own mowing stripes. */}
        <rect y="39.5" width="300" height="181" fill={`url(#${id}-runoff)`} />
        <path d={SURROUND_STRIPES} fill="rgba(255,255,255,0.05)" />

        {/* The court: brighter, freshly striped turf. */}
        <path d={COURT} fill={`url(#${id}-court)`} />
        <path d={COURT_STRIPES} fill="rgba(255,255,255,0.07)" />

        {/* Chalk. */}
        <path
          d={OUTER_LINES}
          {...PAINT_LINE}
          stroke="rgba(255,255,255,0.95)"
          strokeWidth="2"
        />
        <path
          d={INNER_LINES}
          {...PAINT_LINE}
          stroke="rgba(255,255,255,0.88)"
          strokeWidth="1.5"
        />

        {/* Net: shadow on the turf, mesh, white tape, posts in frame. */}
        <path d={NET_SHADOW} fill="rgba(6,24,8,0.22)" />
        <rect
          x="148"
          y={NET_FAR_Y - 5}
          width="4"
          height={NET_NEAR_Y - NET_FAR_Y - 6}
          fill={`url(#${id}-mesh)`}
        />
        <rect
          x="147.6"
          y={NET_FAR_Y - 5}
          width="1.3"
          height={NET_NEAR_Y - NET_FAR_Y - 6}
          fill="#f4f7ef"
        />
        <rect
          x="146.8"
          y={NET_FAR_Y - 7}
          width="6.4"
          height="7"
          rx="1"
          fill="#1c4a2b"
        />
        <rect
          x="146.8"
          y={NET_FAR_Y - 7}
          width="6.4"
          height="1.6"
          rx="0.8"
          fill="#e9efe6"
        />
        <rect
          x="145.5"
          y={NET_NEAR_Y - 17}
          width="9"
          height="17"
          rx="1.5"
          fill="#1c4a2b"
        />
        <rect
          x="145.5"
          y={NET_NEAR_Y - 17}
          width="2.6"
          height="17"
          rx="1"
          fill="rgba(255,255,255,0.22)"
        />
        <rect
          x="145.5"
          y={NET_NEAR_Y - 17}
          width="9"
          height="2.4"
          rx="1.2"
          fill="#f2f5ef"
        />
      </CourtSvg>

      {/* Lighting: floodlight bloom over the wall, a warm key light on the
          turf, and a ground shade toward the camera. */}
      <div className="absolute inset-x-0 top-0 h-[70px] bg-[radial-gradient(ellipse_32%_80%_at_12%_0%,rgba(255,244,214,0.38),transparent_70%),radial-gradient(ellipse_32%_80%_at_88%_0%,rgba(255,244,214,0.3),transparent_70%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_60%_at_32%_42%,rgba(255,246,200,0.2),transparent_70%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_55%,rgba(4,18,8,0.4)_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_90%_110%_at_50%_45%,transparent_55%,rgba(0,0,0,0.42)_100%)]" />
    </>
  );
}

const TennisBall = ({ className }: { className?: string }) => (
  <div
    className={cn(
      "absolute overflow-hidden rounded-full bg-[radial-gradient(circle_at_34%_30%,#f6ff9a_0%,#e2ff1f_38%,#a9c800_100%)] shadow-[inset_-1.5px_-2px_3px_rgba(0,0,0,0.35),0_0_10px_rgba(223,255,0,0.35)]",
      className,
    )}
  >
    <div className="absolute top-[-20%] bottom-[-20%] left-[-62%] w-[85%] rounded-[100%] border-[1.5px] border-white/85" />
    <div className="absolute top-[-20%] right-[-62%] bottom-[-20%] w-[85%] rounded-[100%] border-[1.5px] border-white/85" />
  </div>
);

export function TennisCourtPreview({
  mode: _mode,
  yourTeam,
  opponentTeam,
  yourWon,
  linkToProfiles = false,
  matchScore,
  sets,
}: TennisCourtPreviewProps) {
  const opponentWon = opponentTeam.length > 0 && yourWon === false;
  const yourIsWinner = !!yourWon && !opponentWon;

  const hasScore =
    !!matchScore &&
    (matchScore.yourSets > 0 ||
      matchScore.opponentSets > 0 ||
      (sets?.length ?? 0) > 0);
  const matchState = getRacketMatchState(yourTeam, opponentTeam, hasScore);

  return (
    <RacketMatchFrame
      label="Tennis"
      frameClassName="bg-[#14361e]"
      matchState={matchState}
      scene={<TennisCourtArt />}
      decor={
        matchState === "FULL" ? (
          <>
            <PropShadow className="top-[86%] left-[33%] h-[6px] w-[18px]" />
            <TennisBall className="top-[81%] left-[34%] h-[12px] w-[12px] rotate-[25deg]" />
          </>
        ) : (
          <>
            {/* A ball mid-bounce in the right service box, its shadow on
                the turf below. */}
            <PropShadow className="top-[67%] left-[61.2%] h-[6px] w-[20px]" />
            <TennisBall className="top-[55%] left-[62.5%] h-[15px] w-[15px] rotate-[18deg]" />
          </>
        )
      }
      yourTeam={yourTeam}
      opponentTeam={opponentTeam}
      yourWinner={yourIsWinner}
      opponentWinner={opponentWon}
      linkToProfiles={linkToProfiles}
      center={
        matchState === "FULL" && matchScore ? (
          <RacketScore
            left={matchScore.yourSets}
            right={matchScore.opponentSets}
            leftWinner={yourIsWinner}
            rightWinner={opponentWon}
            breakdown={sets}
            accentClassName="bg-[#dfff00] shadow-[0_0_12px_rgba(223,255,0,0.7)]"
          />
        ) : null
      }
    />
  );
}
